import { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Close, NoteAdd, UploadFile } from '@mui/icons-material';
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Typography,
} from '@mui/material';

import {
	PrimaryButton,
	RED_COLOR_SX,
} from 'src/components/common/buttons/PrimaryButton';
import { snackbar } from 'src/components/common/snackbar/Snackbar';
import {
	IPatientAssetUploadRequest,
	IUploadDialogProps,
	PATIENT_ASSET_LOADING_KEY,
	PatientAssetType,
} from 'src/features/patient_asset';
import { uploadPatientAssets } from 'src/features/patient_asset/store/patientAssetThunks';
import { AppDispatch } from 'src/store';
import { snakeToTitleCase } from 'src/utils/formatter';

import FilePreview from './FilePreview';

const PatientAssetUploadModal: React.FC<IUploadDialogProps> = ({
	isOpen,
	closeUploadDialog,
	onUploadSuccess,
	question,
}) => {
	const { patientId, studyId, siteSlug } = useParams();
	const dispatch = useDispatch<AppDispatch>();
	const isLoading = useSelector((state: { loading: ILoadingState }) => {
		return state.loading.loadingStates[PATIENT_ASSET_LOADING_KEY];
	});

	const [file, setFile] = useState<File | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const fileType = question.type as PatientAssetType;

	const validInputTypes = useMemo(() => {
		switch (fileType) {
			case 'image':
				return 'image/jpeg,image/jpg,image/png';
			case 'file':
				return 'application/pdf';
			default:
				return '';
		}
	}, [fileType]);

	const inputId = `upload-input-${question.id}`;

	const isValidFile = (file: File): boolean => {
		const validTypes = validInputTypes.split(',');
		const isTypeValid = validTypes.includes(file.type);
		const maxSize = Number(
			process.env.REACT_APP_MAX_FILE_UPLOAD_SIZE || 83886080,
		);

		return isTypeValid && file.size <= maxSize;
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files[0]) {
			const fileToUpload = event.target.files[0];
			if (!fileToUpload || !isValidFile(fileToUpload)) {
				snackbar(
					`Invalid file. Make sure it's a valid ${fileType} and under 80MB.`,
					'error',
				);
				setFile(null);
				inputRef.current!.value = '';
				return;
			}
			setFile(fileToUpload);
		}
	};

	const formatRequestData = (): IPatientAssetUploadRequest[] | null => {
		if (!patientId || !studyId || !file) return null;

		return [
			{
				patient_id: Number(patientId),
				study_id: Number(studyId),
				file,
				source: 'questionnaire',
				questionnaire_field_id: question.id,
				type: fileType,
				file_key: 'file_1',
			},
		];
	};

	const handleClose = () => {
		if (isLoading) return;
		setFile(null);
		inputRef.current!.value = '';
		closeUploadDialog();
	};

	const handleSave = async () => {
		if (!siteSlug) return;

		if (file === null) {
			snackbar(`Please select a ${fileType} to upload.`, 'error');
			return;
		}

		const requestData = formatRequestData();
		if (!requestData) return;

		dispatch(
			uploadPatientAssets({
				request: requestData,
				siteSlug: siteSlug,
				onSuccess: (response) => {
					const formattedAssetData = response.flatMap((item) =>
						item.uploaded_assets.map((asset) => {
							const formattedData = {
								id: asset.id,
								url: asset.url,
								thumbnail_url: asset.thumbnail_url,
							};

							return formattedData;
						}),
					);
					onUploadSuccess(formattedAssetData);
					closeUploadDialog();

					setFile(null);
					if (inputRef.current) inputRef.current.value = '';

					snackbar(
						`${snakeToTitleCase(fileType)} uploaded successfully.`,
						'success',
					);
				},
			}),
		);
	};

	if (!isOpen) return null;

	return (
		<>
			<Dialog
				open={isOpen}
				onClose={handleClose}
				maxWidth='sm'
				fullWidth
				PaperProps={{
					sx: {
						borderRadius: '1rem',
						width: '600px',
						height: '506px',
					},
				}}
			>
				<DialogTitle
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						paddingBottom: '0px',
					}}
				>
					<Typography fontSize={'20px'} textTransform={'capitalize'}>
						Upload {snakeToTitleCase(fileType)}
					</Typography>

					<IconButton onClick={handleClose} disabled={isLoading}>
						<Close
							sx={{
								cursor: 'pointer',
								width: '24px',
								height: '24px',

								color: isLoading ? 'rgba(0,0,0,.6)' : 'black',
							}}
						/>
					</IconButton>
				</DialogTitle>
				<DialogContent>
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'column',
							gap: 2,
							paddingTop: '10px',
						}}
					>
						<Box
							sx={{
								width: 'full',
								height: '370px',
							}}
						>
							{file ? (
								<FilePreview file={file} type={fileType} />
							) : (
								<Box
									sx={{
										border: 1,
										borderStyle: 'dashed',
										width: '100%',
										height: '100%',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
									}}
								>
									<Box>
										<Box
											sx={{
												cursor: 'pointer',
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
											}}
											onClick={() =>
												document
													.getElementById(inputId)
													?.click()
											}
										>
											<NoteAdd
												sx={{
													color: '#2196F3',
												}}
											/>
											<Typography
												sx={{
													textDecorationLine:
														'underline',
													color: '#2196F3',
												}}
											>
												Click to upload the{' '}
												{fileType.toLowerCase()}
											</Typography>
										</Box>
									</Box>
								</Box>
							)}
						</Box>

						<DialogActions
							sx={{
								display: 'flex',
								justifyContent: 'space-between',
								padding: '0px',
							}}
						>
							{file && (
								<>
									<Box
										onClick={
											isLoading
												? undefined
												: () =>
														document
															.getElementById(
																inputId,
															)
															?.click()
										}
										sx={{
											cursor: isLoading
												? 'not-allowed'
												: 'pointer',
											userSelect: 'none',
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											opacity: isLoading ? 0.5 : 1,
											pointerEvents: isLoading
												? 'none'
												: 'auto',
										}}
									>
										<Typography
											sx={{
												textDecorationLine: 'underline',
												color: '#00000099',
											}}
										>
											Replace {snakeToTitleCase(fileType)}
										</Typography>
										<UploadFile
											sx={{ color: '#00000099' }}
										/>
									</Box>
								</>
							)}

							<PrimaryButton
								fullWidth={false}
								onClick={handleSave}
								isLoading={isLoading}
								sx={{ ...RED_COLOR_SX, ml: 'auto' }}
							>
								{isLoading ? 'Uploading' : 'Upload'}
							</PrimaryButton>
						</DialogActions>
					</Box>
				</DialogContent>
				<input
					ref={inputRef}
					type='file'
					id={inputId}
					accept={validInputTypes}
					onChange={handleFileChange}
					hidden
				/>
			</Dialog>
		</>
	);
};

export default PatientAssetUploadModal;
